/* ============================================================
   AndroidDefaultBuilder.jsx — ADMIN: bina DEFAULT kongsi (Lapis 1)
   ============================================================
   Tempat ADMIN bina + verify flow post telefon sampai smooth, kemudian
   "Save as default" → kedudukan button (ratio) dihantar ke SEMUA user
   sebagai default (androidDefault → doc Firestore world-read). Guna enjin
   SAMA (androidRunner) macam page customer — jadi apa yang admin verify di
   sini = apa yang user dapat.

   Alir: Connect phone → pilih card ada video → Run (record) → Stepper +
   mirror hidup. Lulus hujung-ke-hujung (draft) → Save as default.
   · "Flow changed" dicentang = naik VERSION (paksa SEMUA user re-calibrate,
     untuk bila Shopee tukar UI). Tak dicentang = tuning posisi je (user
     sedia ada TAK terganggu — koordinat mereka menang).
   ⚠️ Save = tulis ke doc Firestore world-read → PERLU rules dibenarkan dulu
     (admin-write). Sampai itu, Save gagal dgn mesej jelas (tiada bahaya).
   UI admin = English. Guna semula kelas .pa-* (PhoneAutomationView.css).
   ============================================================ */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Smartphone, RefreshCw, Loader2, PlayCircle, CircleCheck, CircleSlash, Circle, Sparkles, Camera, Home, ArrowLeft, Save, Star, ScanText } from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import './PhoneAutomationView.css';
import { AndroidController, isWebUsbSupported, AdbError, parseUiXml, phoneVideoDest } from '../utils/androidAdb';
import { runRecipe } from '../utils/androidRunner';
import { recipeFor } from '../utils/androidRecipe';
import { loadCalib } from '../utils/androidCalibration';
import { loadAdminDefault, saveAdminDefault } from '../utils/androidDefault';
import { captionUntukPhone } from '../utils/androidPostCheck';

const APP_ID = 'shopee'; // app sedia untuk default (tambah kemudian)

function msOf(t) {
  if (t && typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t === 'number') return t;
  return 0;
}

export default function AndroidDefaultBuilder() {
  const supported = isWebUsbSupported();
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [pickCard, setPickCard] = useState('');
  const [linkOverride, setLinkOverride] = useState(''); // link produk utk test (admin paste)
  const [steps, setSteps] = useState([]);       // Stepper [{id,label,status,note}]
  const [screen, setScreen] = useState('');       // mirror dataUrl
  const [note, setNote] = useState('');
  const [phase, setPhase] = useState('idle');     // idle|working|done|failed
  const [ranOk, setRanOk] = useState(false);      // larian terakhir lulus (boleh Save)
  const [bumpVersion, setBumpVersion] = useState(false);
  const [defInfo, setDefInfo] = useState({ version: null, count: 0 });
  const [reading, setReading] = useState(''); // hasil "Read screen" (apa robot nampak)
  const [typeText, setTypeText] = useState(''); // teks alat ukur "Type on phone"
  const [realPost, setRealPost] = useState(false); // uji laluan Post sebenar (terbit awam)
  const ctlRef = useRef(null);
  const imgRef = useRef(null);
  const pressRef = useRef(null);

  const say = useCallback((m) => setNote(m), []);
  const getCtl = useCallback(() => {
    if (!ctlRef.current) ctlRef.current = new AndroidController(() => {}, () => { setConnected(false); setDeviceName(''); });
    return ctlRef.current;
  }, []);

  useEffect(() => () => { if (ctlRef.current) ctlRef.current.disconnect().catch(() => {}); }, []);

  // Papar version default semasa (dari doc / fallback kod).
  useEffect(() => { loadAdminDefault(APP_ID).then((d) => setDefInfo({ version: d.version, count: Object.keys(d.positions || {}).length })).catch(() => {}); }, []);

  const handleConnect = useCallback(async (reconnect) => {
    if (busy) return;
    setBusy(true);
    try {
      const ctl = getCtl();
      if (reconnect) await ctl.reconnect(); else await ctl.connect();
      const info = await ctl.getDeviceInfo();
      setConnected(true);
      setDeviceName(`${info.manufacturer || ''} ${info.model || ''}`.trim() + ` · ${info.screenWidth}×${info.screenHeight}`);
      say('Phone connected. Pick a card and Run (record).');
    } catch (e) {
      setConnected(false);
      say(e instanceof AdbError ? e.message : 'Could not connect. Check cable and USB debugging.');
    } finally { setBusy(false); }
  }, [busy, getCtl, say]);

  const loadCards = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) { say('Sign in to load cards.'); return; }
    try {
      const snap = await getDocs(collection(db, 'users', uid, 'products'));
      const out = [];
      snap.forEach((d) => {
        const data = d.data() || {}; const pr = data.prompts || {};
        const sk = ['set1', 'set2', 'set3'].find((k) => typeof pr[k]?.uploadedVideo === 'string' && /^https?:\/\//.test(pr[k].uploadedVideo));
        if (sk) out.push({
          id: d.id, name: data.name || d.id, setKey: sk, url: pr[sk].uploadedVideo, ms: msOf(data.lastUpdated),
          caption: captionUntukPhone(pr[sk]),                 // caption sebenar card (kotak FB)
          link: (data.shopeeLink || data.link || '').trim(),  // link produk (tag)
        });
      });
      out.sort((a, b) => b.ms - a.ms);
      setCards(out);
      setPickCard((p) => (out.some((c) => c.id === p) ? p : (out[0]?.id || '')));
    } catch { say('Could not load cards.'); }
  }, [say]);
  useEffect(() => { loadCards(); }, [loadCards]);

  const selected = useMemo(() => cards.find((c) => c.id === pickCard) || null, [cards, pickCard]);
  // Prefill link dari card bila tukar card (admin boleh override — guna link apa saja utk test).
  useEffect(() => { setLinkOverride(selected?.link || ''); }, [selected]);

  const run = useCallback(async () => {
    if (!connected) { say('Connect first.'); return; }
    if (!selected) { say('Pick a card with a video.'); return; }
    if (busy) return;
    const recipe = recipeFor(APP_ID);
    if (!recipe) { say('No recipe.'); return; }
    setBusy(true); setPhase('working'); setRanOk(false);
    setSteps(recipe.steps.map((s) => ({ id: s.id, label: s.labelEn, status: 'pending', note: '' })));
    try {
      const ctl = getCtl();
      const serial = ctl.info?.serial || '';
      say('Transferring the video…');
      const res = await fetch(selected.url);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const dest = phoneVideoDest(selected.name, selected.id, selected.setKey);
      await ctl.pushVideo(blob, dest, () => {});
      await ctl.mediaScan(dest);
      say(realPost ? 'Running the recipe (this PUBLISHES a real post)…' : 'Running the recipe (records positions; always saved as Draft)…');
      const prodLink = (linkOverride || selected.link || '').trim();
      const result = await runRecipe(ctl, {
        appId: APP_ID, serial, ...(realPost ? { mode: 'post' } : { calibrating: true }),
        // 100% workflow: caption (card) + link produk (kotak admin / card).
        params: { caption: selected.caption || selected.name || 'SESAAT', productLink: prodLink, videoDest: dest },
      }, {
        onStep: (st) => setSteps((prev) => { const n = prev.slice(); if (n[st.index]) n[st.index] = { ...n[st.index], status: st.status, note: st.note }; return n; }),
        onScreen: (url) => setScreen(url),
        onLog: (m, level) => {
          if (level === 'err' || level === 'ok') say(m);
          setReading((p) => `${p.startsWith('RUN LOG') ? p : 'RUN LOG'}\n${m}`);
        },
      });
      if (result.status === 'done') {
        setPhase('done');
        say('Posted and confirmed on the profile ✓');
      } else if (result.status === 'draft_saved') {
        setPhase('done'); setRanOk(true);
        say('All steps passed their checks ✓. Save as default is now available.');
      } else {
        setPhase('failed');
        say(realPost ? `Run failed at the red step: ${result.reason}. Check the profile before running again.` : `Run failed at the red step. ${result.draftSaved ? `An INCOMPLETE Draft was saved (missing: ${(result.belumSiap || []).join(', ') || 'none listed'}).` : 'No Draft confirmed.'} Nothing was published.`);
      }
    } catch (e) {
      setPhase('failed');
      say(e instanceof AdbError ? e.message : 'Something went wrong. Try Reconnect.');
    } finally { setBusy(false); }
  }, [connected, selected, linkOverride, realPost, busy, getCtl, say]);

  const capture = useCallback(async () => {
    if (!connected) return;
    try { const s = await getCtl().screenshot(); if (s?.dataUrl) setScreen(s.dataUrl); } catch { /* biar */ }
  }, [connected, getCtl]);
  const phoneKey = useCallback((fn) => async () => {
    if (!connected) return;
    try { await getCtl()[fn](); setTimeout(() => { capture(); }, 500); } catch { /* biar */ }
  }, [connected, getCtl, capture]);

  // Tekan mirror = tekan telefon (seret = swipe). Koordinat ikut saiz sebenar skrin telefon.
  const toPhone = (e) => {
    const img = imgRef.current;
    if (!img) return null;
    const r = img.getBoundingClientRect();
    // Gambar object-fit: contain → ada ruang hitam kiri/kanan. Tolak dulu, kalau tidak X tersasar.
    const scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
    const offX = (r.width - img.naturalWidth * scale) / 2;
    const offY = (r.height - img.naturalHeight * scale) / 2;
    return { x: (e.clientX - r.left - offX) / scale, y: (e.clientY - r.top - offY) / scale };
  };
  const onMirrorDown = (e) => { pressRef.current = toPhone(e); };
  const onMirrorUp = async (e) => {
    const a = pressRef.current; const b = toPhone(e); pressRef.current = null;
    if (!a || !b || !connected || busy) return;
    try {
      if (Math.hypot(b.x - a.x, b.y - a.y) < 10) await getCtl().tap(a.x, a.y);
      else await getCtl().swipe(a.x, a.y, b.x, b.y, 300);
      setTimeout(() => { capture(); }, 900);
    } catch (err) { say('Tap failed: ' + (err?.message || err)); }
  };

  // Taip ke medan yang sedang fokus di telefon (alat ukur admin). Ruang kekal ikut inputText.
  const typeOnPhone = useCallback(async () => {
    const t = typeText.trim();
    if (!connected || busy || !t) return;
    try {
      // "key:122 112" = hantar kekunci Android (alat ukur), selain itu taip teks.
      const keys = t.match(/^key:([\d ]+)$/);
      if (keys) { for (const code of keys[1].trim().split(/\s+/)) await getCtl()._run(['input', 'keyevent', code], `Key ${code}`); }
      else if (/^combo:[\d ]+$/.test(t)) await getCtl()._run(['input', 'keycombination', ...t.slice(6).trim().split(/\s+/)], `Combo ${t}`);
      else if (/^shell:/.test(t)) { // alat ukur admin: jawapan phone keluar di kotak Read screen
        const out = await getCtl().adb.subprocess.noneProtocol.spawnWaitText(t.slice(6).trim());
        setReading(`$ ${t.slice(6).trim()}\n${out}`);
      }
      else await getCtl().inputText(t);
      setTimeout(() => { capture(); }, 900);
    } catch (err) { say('Type failed: ' + (err?.message || err)); }
  }, [typeText, connected, busy, getCtl, capture, say]);

  // Read screen: tunjuk TEPAT apa robot nampak — tetingkap fokus + senarai elemen, atau
  // ralat sebenar kalau skrin tak dapat dibaca. Inilah alat ukur sebelum tetapkan langkah.
  const readScreen = useCallback(async () => {
    if (!connected || busy) return;
    setBusy(true);
    const ctl = getCtl();
    let focus = '';
    try { focus = await ctl.getFocusedWindow(); } catch (e) { focus = 'FOCUS ERROR: ' + (e?.message || e); }
    let body;
    try {
      const rows = parseUiXml(await ctl.dumpUi())
        .filter((n) => n.bounds && (n.text || n.desc || n.id || n.clickable || /switch|edittext|checkbox|toggle/i.test(n.cls)));
      body = `${rows.length} elements\n` + rows.slice(0, 250).map((n) => {
        const b = n.bounds;
        return `${n.cls} [${b.x1},${b.y1}][${b.x2},${b.y2}]${n.clickable ? ' CLICK' : ''}${n.checked ? ' CHECKED' : ''} text="${n.text}" desc="${n.desc}" id="${n.id.split('/').pop()}"`;
      }).join('\n');
    } catch (e) { body = 'READ FAILED: ' + (e?.message || e); }
    setReading(`${focus || 'focus: (empty)'}\n${body}`);
    await capture();
    setBusy(false);
  }, [connected, busy, getCtl, capture]);

  // Save as default: koordinat admin (dari larian) → RATIO → doc Firestore.
  const saveDefault = useCallback(async () => {
    const ctl = getCtl();
    const serial = ctl.info?.serial || '';
    const sw = ctl.info?.screenWidth || 0, sh = ctl.info?.screenHeight || 0;
    if (!serial || !sw || !sh) { say('Connect + run first.'); return; }
    const calib = loadCalib(APP_ID, serial);
    const positions = {};
    Object.entries(calib.coords || {}).forEach(([k, v]) => {
      const x = Number(v?.x), y = Number(v?.y);
      if (Number.isFinite(x) && Number.isFinite(y) && x > 0 && y > 0) positions[k] = { rx: x / sw, ry: y / sh };
    });
    if (!Object.keys(positions).length) { say('No recorded positions yet — run a smooth test first.'); return; }
    setBusy(true);
    try {
      const r = await saveAdminDefault(APP_ID, positions, { bumpVersion });
      setDefInfo({ version: r.version, count: r.count });
      say(`Saved as default ✓ — version ${r.version}, ${r.count} positions. All users will use this${bumpVersion ? ' and re-calibrate (version bumped)' : ''}.`);
    } catch (e) {
      say('Save failed — the shared default doc may not be writable yet (rules not deployed). ' + (e?.message || e));
    } finally { setBusy(false); }
  }, [getCtl, bumpVersion, say]);

  return (
    <section className="s-card pa-card" style={{ marginBottom: 18 }}>
      <div className="pa-rowhead">
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Star size={18} style={{ color: 'var(--s-brand)' }} />
          <b style={{ fontSize: 15 }}>Build shared default (recipe)</b>
        </span>
        <span className="pa-btns">
          <span className="s-badge s-badge--neutral">Default v{defInfo.version ?? '—'} · {defInfo.count} pos</span>
          {!connected && <button className="s-btn s-btn--primary s-btn--sm" onClick={() => handleConnect(false)} disabled={!supported || busy}>{busy ? <Loader2 size={14} className="pa-spin" /> : <Smartphone size={14} />} Connect</button>}
          <button className="s-btn s-btn--sm" onClick={() => handleConnect(true)} disabled={!supported || busy}><RefreshCw size={14} /> Reconnect</button>
        </span>
      </div>
      <p className="pa-hint" style={{ marginTop: 6 }}>
        Admin: run the Shopee post flow until it is smooth, then <b>Save as default</b> for all users. {connected ? `Connected: ${deviceName}` : 'Not connected.'}
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        {cards.length > 0 ? (
          <select className="s-select" style={{ flex: '1 1 320px' }} value={pickCard} onChange={(e) => setPickCard(e.target.value)} disabled={busy}>
            {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : <span className="pa-hint">No cards with a video yet.</span>}
        <button className="s-btn s-btn--sm" onClick={loadCards} disabled={busy}><RefreshCw size={14} /> Reload</button>
        <button className="s-btn s-btn--primary" onClick={run} disabled={!connected || !selected || busy}>
          {busy && phase === 'working' ? <Loader2 size={16} className="pa-spin" /> : <PlayCircle size={16} />} Run (record)
        </button>
      </div>

      <div style={{ margin: '0 0 6px' }}>
        <label style={{ fontSize: 12, color: 'var(--s-ink-2)', display: 'block', marginBottom: 4 }}>
          Shopee product link (for the Tag product step)
        </label>
        <input
          className="s-input"
          style={{ width: '100%' }}
          type="text"
          placeholder="https://s.shopee.com.my/…"
          value={linkOverride}
          onChange={(e) => setLinkOverride(e.target.value)}
          disabled={busy}
        />
        <p className="pa-hint" style={{ marginTop: 4 }}>Prefilled from the card. The robot pastes this link in Add Product → Import URL.</p>
        <label className="pa-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <input type="checkbox" checked={realPost} onChange={(e) => setRealPost(e.target.checked)} disabled={busy} />
          Real Post test: publishes publicly, then confirms it on the profile
        </label>
      </div>

      <div className="pa-test-grid">
        <div className="pa-test-left">
          {steps.length > 0 && (
            <ol className="pa-stepper">
              {steps.map((st) => (
                <li key={st.id} className={`pa-step pa-step--${st.status}`}>
                  <span className="pa-step__ic" aria-hidden="true">
                    {st.status === 'running' ? <Loader2 size={15} className="pa-spin" />
                      : st.status === 'pass' || st.status === 'healed' ? <CircleCheck size={15} />
                        : st.status === 'error' ? <CircleSlash size={15} />
                          : <Circle size={15} />}
                  </span>
                  <span className="pa-step__lbl">{st.label}</span>
                  {st.status === 'healed' && <span className="pa-step__tag"><Sparkles size={11} /> AI adjusted</span>}
                  {st.status === 'error' && st.note && <span className="pa-step__err" title={st.note}>{st.note}</span>}
                </li>
              ))}
            </ol>
          )}

          {note && (
            <div className={`pa-note ${phase === 'failed' ? 'bad' : (phase === 'done' || (connected && !busy)) ? 'good' : ''}`}>
              {busy ? <Loader2 size={15} className="pa-spin" />
                : phase === 'failed' ? <CircleSlash size={15} />
                  : (phase === 'done' || connected) ? <CircleCheck size={15} />
                    : <Circle size={15} />}
              <span>{note}</span>
            </div>
          )}

          {connected && (
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <input className="s-input" style={{ flex: 1 }} type="text" placeholder="Type on phone (focused field)" value={typeText} onChange={(e) => setTypeText(e.target.value)} disabled={busy} />
              <button className="s-btn s-btn--sm" onClick={typeOnPhone} disabled={busy || !typeText.trim()} type="button">Type</button>
            </div>
          )}

          {reading && <pre className="pa-reading">{reading}</pre>}

          {ranOk && (
            <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--s-line)', borderRadius: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                <input type="checkbox" checked={bumpVersion} onChange={(e) => setBumpVersion(e.target.checked)} />
                Flow changed (Shopee UI different) — bump version &amp; make all users re-calibrate
              </label>
              <button className="s-btn s-btn--primary" onClick={saveDefault} disabled={busy}>
                {busy ? <Loader2 size={15} className="pa-spin" /> : <Save size={15} />} Save as default (all users)
              </button>
              <p className="pa-hint" style={{ marginTop: 8 }}>
                Saves this phone&apos;s button positions as ratios. Users&apos; own fixes still win over this default.
              </p>
            </div>
          )}
        </div>

        <div className="pa-mirror">
          <div className="pa-mirror__bar">
            <span className="pa-mirror__title">Phone screen</span>
            <span className="pa-mirror__ctl">
              <button className="s-btn s-btn--sm" onClick={readScreen} disabled={!connected || busy} title="Read screen" aria-label="Read screen" type="button"><ScanText size={14} /></button>
              <button className="s-btn s-btn--sm" onClick={capture} disabled={!connected || busy} title="Capture" type="button"><Camera size={14} /></button>
              <button className="s-btn s-btn--sm" onClick={phoneKey('home')} disabled={!connected || busy} title="Home" type="button"><Home size={14} /></button>
              <button className="s-btn s-btn--sm" onClick={phoneKey('back')} disabled={!connected || busy} title="Back" type="button"><ArrowLeft size={14} /></button>
            </span>
          </div>
          <div className="pa-mirror__screen">
            {screen ? <img ref={imgRef} src={screen} alt="Phone screen" draggable={false} style={{ cursor: connected && !busy ? 'pointer' : 'default' }} onPointerDown={onMirrorDown} onPointerUp={onMirrorUp} /> : <span className="pa-mirror__empty"><Smartphone size={24} />Run to see the phone live, or press Capture.</span>}
          </div>
        </div>
      </div>
    </section>
  );
}
